import { logSentryConsoleWarning } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import type {
	AgentSession,
	AgentSessionContext,
	AgentSessionState,
	NormalizedConversationChannel,
	RawSessionNode,
} from './types';

const AGENT_SESSION_STATE_VALUES = new Set<unknown>([
	'WORKING',
	'INPUT_REQUIRED',
	'ARTEFACT_READY',
	'AUTH_REQUIRED',
	'FAILED',
	'REJECTED',
	'COMPLETED',
	'CANCELLED',
	'UNKNOWN',
] satisfies AgentSessionState[]);

const isAgentSessionState = (value: unknown): value is AgentSessionState =>
	AGENT_SESSION_STATE_VALUES.has(value);

const getHttpUrl = (value: string | null | undefined, base?: string | null): string | undefined => {
	if (!value?.trim()) {
		return undefined;
	}

	try {
		const url = new URL(value.trim(), base?.trim() || undefined);
		return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined;
	} catch {
		return undefined;
	}
};

const getConversationChannel = (
	channel: RawSessionNode['conversationChannel'],
): NormalizedConversationChannel | undefined => {
	if (channel == null) {
		return undefined;
	}

	return {
		...(channel.id == null ? {} : { id: channel.id }),
		...(channel.name == null ? {} : { name: channel.name }),
		...(channel.createdAt === undefined ? {} : { createdAt: channel.createdAt }),
		...(channel.interactionChannel === undefined
			? {}
			: { interactionChannel: channel.interactionChannel }),
		...(channel.interactionChannelAri === undefined
			? {}
			: { interactionChannelAri: channel.interactionChannelAri }),
		...(channel.lastAgentMessageId === undefined
			? {}
			: { lastAgentMessageId: channel.lastAgentMessageId }),
		...(channel.lastAgentMessageStatus === undefined
			? {}
			: { lastAgentMessageStatus: channel.lastAgentMessageStatus }),
		...(channel.lastMessage === undefined ? {} : { lastMessage: channel.lastMessage }),
		...(channel.lastMessageTimestamp === undefined
			? {}
			: { lastMessageTimestamp: channel.lastMessageTimestamp }),
		...(channel.lastMessageType === undefined ? {} : { lastMessageType: channel.lastMessageType }),
		...(channel.pinnedAt === undefined ? {} : { pinnedAt: channel.pinnedAt }),
		...(channel.status === undefined ? {} : { status: channel.status }),
		...(channel.readStatus === undefined ? {} : { readStatus: channel.readStatus }),
		...(channel.isPinned === undefined ? {} : { isPinned: channel.isPinned }),
		...(channel.isPlanning === undefined ? {} : { isPlanning: channel.isPlanning }),
	};
};

const getDisplayAgent = (
	session: RawSessionNode,
	sessionId: string,
): AgentSession['displayAgent'] => {
	if (session.agent != null) {
		return {
			id: session.agent.accountId,
			name: session.agent.name,
			...(session.agent.picture ? { avatarUrl: session.agent.picture } : {}),
			...(session.isRovo == null ? {} : { isRovo: session.isRovo }),
			...(session.convoAiAgentId == null ? {} : { agentId: session.convoAiAgentId }),
			...(session.agentAccountId == null ? {} : { agentIdentityAccountId: session.agentAccountId }),
		};
	}

	if (session.studioAgent?.name != null) {
		return {
			id: session.convoAiAgentId ?? session.agentAccountId ?? sessionId,
			name: session.studioAgent.name,
			...(session.isRovo == null ? {} : { isRovo: session.isRovo }),
			...(session.convoAiAgentId == null ? {} : { agentId: session.convoAiAgentId }),
			...(session.agentAccountId == null ? {} : { agentIdentityAccountId: session.agentAccountId }),
		};
	}

	return undefined;
};

const getContexts = (session: RawSessionNode): readonly AgentSessionContext[] | undefined => {
	const contexts = session.associatedEntities?.flatMap((entity): AgentSessionContext[] => {
		if (entity?.__typename === 'JiraIssue') {
			const issueKey = entity.key?.trim();
			const url = getHttpUrl(entity.webUrl);
			return issueKey && url ? [{ id: entity.id, label: issueKey, type: 'jira', url }] : [];
		}

		if (entity?.__typename === 'ConfluencePage') {
			const title = entity.title?.trim();
			const url = getHttpUrl(entity.links?.webUi, entity.links?.base);
			if (!title || !url) {
				return [];
			}

			return [
				{
					id: entity.id,
					label: title,
					type: 'confluence',
					url,
					subtype: 'page',
				},
			];
		}

		return [];
	});

	return contexts?.length ? contexts : undefined;
};

export const normalizeAgentSession = (session: RawSessionNode): AgentSession | null => {
	if (session.id == null) {
		return null;
	}

	const conversationChannel = getConversationChannel(session.conversationChannel);
	const title = conversationChannel?.name?.trim();
	const readStatus = conversationChannel?.readStatus;
	const state = session.state ?? undefined;
	if (state != null && !isAgentSessionState(state)) {
		logSentryConsoleWarning('Unknown agent session state returned by AgentSessions API:', state);
	}
	const displayAgent = getDisplayAgent(session, session.id);
	const contexts = getContexts(session);
	const legacyContext = contexts?.[0]?.label.trim();

	return {
		id: session.id,
		raw: session,
		hasConversation: conversationChannel != null,
		...(session.lastStateChangeAt == null ? {} : { lastStateChangeAt: session.lastStateChangeAt }),
		...(state == null ? {} : { state }),
		...(session.stateReason === undefined ? {} : { stateReason: session.stateReason }),
		...(conversationChannel == null ? {} : { conversationChannel }),
		...(conversationChannel?.id == null ? {} : { conversationId: conversationChannel.id }),
		...(title ? { title } : {}),
		...(readStatus == null ? {} : { isUnread: !readStatus }),
		...(session.conversationChannel?.lastAgentMessageStatus === 'IN_PROGRESS'
			? { isInProgress: true }
			: {}),
		...(state == null
			? {}
			: { requiresInput: state === 'INPUT_REQUIRED' || state === 'AUTH_REQUIRED' }),
		...(displayAgent == null ? {} : { displayAgent }),
		...(contexts == null ? {} : { contexts }),
		...(legacyContext ? { context: legacyContext } : {}),
	};
};
